import {
  ADMIN_FEES_PERCENT_MAX,
  ADMIN_FEES_PERCENT_MIN,
} from '../../constants/event.constants';
import {
  AddOn,
  AdminFee,
  BigBlindAnte,
  Bounty,
  Event,
  EventGameLimit,
  EventGameType,
  EventPrivacy,
  LateEntry,
  Location,
  ReEntry,
  Rebuy,
} from '../../models/event';
import * as ERROR_MESSAGES from '../../constants/error-messages.constants';
export type AdminFees_schema = { [payee: string]: { percent: number } };

export const NO_VALUE_PLACEHOLDER = -1;
export type Rebuy_schema = {
  rebuys_allowed?: number | string;
  rebuy_cutoff?: number;
  rebuy_price?: number;
  rebuy_chips?: number;
};

export type AddOn_schema = {
  add_on_price?: number;
  add_on_chips?: number;
  add_on_instructions?: string;
};

export type ReEntry_schema = {
  reentries?: number;
  reentry_cutoff?: number;
  reentry_price?: number;
};

export type Bounty_schema = {
  bounty_price?: number;
  bounty_amount?: number;
};

// How attendee data looks on the firestore end
// attendees : event_id: string : { user_id: { method: invited/requested/joined, status: yes/no/none } }
type Method = 'invited' | 'requested' | 'joined';
type Status = 'yes' | 'no' | 'none';

export type Attendees_schema = {
  [user_id: string]: { method: Method; status: Status };
};

export type EventDataSchema = {
  admin_uid: string;
  image_url: string;
  name: string;
  desc: string;
  start: string;
  timezone: string;
  location: Location;
  privacy: EventPrivacy;
  currency: string;
  admin_fees: AdminFees_schema;
  game_limit: EventGameLimit;
  game_type: EventGameType;
  max_players: number;
  buy_in: number;
  seating_mode?: 'random' | 'manual';
  table_count?: number;
  seating_log?: {
    generatedAt: string;
    mode: 'random' | 'manual';
    playersSeated: number;
    tableCount: number;
  }[];
  venue_id?: string;
  structure_id?: string;
  charity_id?: string;
  charity_name?: string;
  charity_pct?: number;
  license_id?: string;
  license_type?: string;
  is_recurring?: boolean;
  recurring_frequency?: string;
  additional_games?: string[];
  cash_game_stakes?: string;
  event_notes?: string;
  blind_level_duration: number;
  blind_structure: {
    [key: string]: {
      level: string;
      label?: string;
      small_blind?: number;
      big_blind?: number;
      ante?: number;
      duration: number;
    };
  };
  rebuy: Rebuy_schema;
  add_on: AddOn_schema;
  starting_chips: number;
  big_blind_ante_round_start: number;
  late_entry_cutoff: number;
  reentry: ReEntry_schema;
  bounty: Bounty_schema;
  attendees: Attendees_schema;
  // Dealer assignment
  dealer_uid?: string;
  dealer_status?: string;
  event_status?: string;
  archived_at?: string;
  dealer_invite_deadline?: string;
  dealer_standby_call_by_time?: string;
  dealer_invite_urgent?: boolean;
};

// Parsing functions
export const eventToSchema = (
  data: Event,
  admin_uid: string
): EventDataSchema => {
  const adminFees = adminFeeToSchema(data.adminFees);
  const rebuy = rebuyToSchema(data.rebuys);
  const add_on = addOnToSchema(data.addOn);
  const big_blind_ante_round_start = bigBlindAnteToSchema(data.bigBlindAnte);
  const late_entry_cutoff = lateEntryToSchema(data.lateEntry);
  const reentry = reEntryToSchema(data.reEntry);
  const bounty = bountyToSchema(data.bounty);
  const blind_structure: {
    [key: string]: {
      level: string;
      label?: string;
      small_blind?: number;
      big_blind?: number;
      ante?: number;
      duration: number;
    };
  } = {};
  if (data.blindStructure && data.blindStructure.length > 0) {
    data.blindStructure.forEach((lvl: any, index: number) => {
      const key = String(index + 1);
      if (lvl.level === 'break') {
        blind_structure[key] = {
          level: 'break',
          ...(lvl.label ? { label: lvl.label } : {}),
          duration: Number(lvl.duration),
        };
      } else {
        blind_structure[key] = {
          level: String(lvl.level),
          ...(lvl.label ? { label: lvl.label } : {}),
          small_blind: Number(lvl.smallBlind),
          big_blind: Number(lvl.bigBlind),
          ante: Number(lvl.ante) || 0,
          duration: Number(lvl.duration),
        };
      }
    });
  }
  return {
    admin_uid,
    image_url: data.imageUrl ? data.imageUrl : '',
    name: data.name,
    desc: data.desc ? data.desc : '',
    start: data.startDateTime,
    timezone: data.timezone,
    location: data.location,
    privacy: data.privacy,
    currency: data.currency,
    admin_fees: adminFees,
    game_limit: data.gameLimit,
    game_type: data.gameType,
    max_players: data.maxPlayers,
    buy_in: data.buyIn,
    seating_mode: data.seatingMode ?? 'random',
    ...(data.tableCount ? { table_count: data.tableCount } : {}),
    ...(data.seatingLog ? { seating_log: data.seatingLog } : {}),
    ...(data.venueId ? { venue_id: data.venueId } : {}),
    structure_id: data.structureId ?? undefined,
    charity_id: data.charityId ?? undefined,
    charity_name: data.charityName ?? undefined,
    charity_pct: data.charityPct ?? 0,
    license_id: data.licenseId ?? undefined,
    license_type: data.licenseType ?? undefined,
    is_recurring: data.isRecurring ?? false,
    recurring_frequency: data.recurringFrequency ?? undefined,
    additional_games: data.additionalGames ?? [],
    cash_game_stakes: data.cashGameStakes ?? undefined,
    event_notes: data.eventNotes ?? undefined,
    starting_chips: data.startingChips,

    blind_level_duration: data.blindLevelDuration,
    blind_structure,
    rebuy,
    add_on,
    big_blind_ante_round_start,
    late_entry_cutoff,
    reentry,
    bounty,
    attendees: {},
    dealer_uid: data.dealerUid ?? undefined,
    dealer_status: data.dealerStatus ?? 'none',
    ...(data.status ? { event_status: data.status } : {}),
    ...((data as any).archived_at ? { archived_at: (data as any).archived_at } : {}),
    dealer_invite_deadline: data.dealerInviteDeadline ?? undefined,
    dealer_standby_call_by_time: data.dealerStandbyCallByTime ?? undefined,
    dealer_invite_urgent: data.dealerInviteUrgent ?? false,
  };
};

export const adminFeeToSchema = (adminFees: AdminFee[]): AdminFees_schema => {
  const adminFeesSchema: { [key: string]: { percent: number } } = {};
  let adminFeesSum = 0;

  if (adminFees && adminFees.length > 0) {
    adminFees.forEach((fee, index) => {
      const payeeKey = adminFees[index].payee;
      if (
        isNaN(fee.percent) ||
        fee.percent <= ADMIN_FEES_PERCENT_MIN ||
        fee.percent > ADMIN_FEES_PERCENT_MAX
      ) {
        throw new Error(ERROR_MESSAGES.ADMIN_FEES_PERCENT_MIN_MAX);
      }
      adminFeesSchema[payeeKey] = {
        percent: fee.percent,
      };
      adminFeesSum += fee.percent;
      if (adminFeesSum > ADMIN_FEES_PERCENT_MAX) {
        throw new Error(ERROR_MESSAGES.ADMIN_FEES_SUM_MIN_MAX);
      }
    });
  }
  return adminFeesSchema;
};

export const rebuyToSchema = (rebuys: Rebuy): Rebuy_schema => {
  if (rebuys.isRebuys) {
    return {
      rebuys_allowed: rebuys.rebuysAllowed,
      rebuy_cutoff: rebuys.rebuyCutoff,
      rebuy_chips: rebuys.rebuyChips,
      rebuy_price: rebuys.rebuyPrice,
    };
  }
  return {};
};

export const addOnToSchema = (addOn: AddOn): AddOn_schema => {
  if (!addOn.isAddOn) {
    return {};
  } else {
    return {
      add_on_chips: addOn.addOnAmount,
      add_on_price: addOn.addOnPrice,
    };
  }
};

export const bigBlindAnteToSchema = (bigBlindAnte: BigBlindAnte): number => {
  if (!bigBlindAnte.isBigBlindAnte) {
    return NO_VALUE_PLACEHOLDER;
  } else {
    return bigBlindAnte.bigBlindAnteRoundStart as number;
  }
};

export const lateEntryToSchema = (lateEntry: LateEntry): number => {
  if (!lateEntry.isLateEntry) {
    return NO_VALUE_PLACEHOLDER;
  } else {
    return lateEntry.lateEntryCutoff as number;
  }
};

export const reEntryToSchema = (reEntry: ReEntry): ReEntry_schema => {
  if (!reEntry.isReEntry) {
    return {};
  } else {
    return {
      reentries: reEntry.reEntries,
      reentry_cutoff: reEntry.reEntryCutoff,
      reentry_price: reEntry.reEntryPrice,
    };
  }
};

export const bountyToSchema = (bounty: Bounty): Bounty_schema => {
  if (!bounty.isBounty) {
    return {};
  } else {
    return {
      bounty_amount: bounty.bountyAmount,
      bounty_price: bounty.bountyPrice,
    };
  }
};
